// WHERE THE PIECES STAND, decided once for both screens (playtest 12).
//
// The TV (layout.js) and the Frontier's phone (frontierLayout.js) each used
// to place units, raiders and a region's landmarks their own way, and they
// drifted: the TV stood every unit in a corner of its hex ("I had asked you
// to move the units to the center of the tile"), the phone stood the Old
// Road's cabin full size on top of a pioneer ("those extra buildings and
// stuff cover up the units"), and the TV never drew the landmarks at all.
// Both layouts now ask this file, so the two maps cannot disagree again.
//
// Pure. Offsets are in world units on a hex whose centre-to-corner is 0.577.
import { isTown, key } from '@/engine/map';
import { rivalGround } from '@/engine/rivals';

/** How far from the middle a piece stands when it is sharing its hex. */
const SHARE_R = 0.25;
// Where sharers go, in turn: front-left, front-right, then round the back,
// so the first two are never hidden behind whatever stands in the middle.
const SHARE_ANGLES = [150, 30, 270, 210, 330, 90].map((d) => (d * Math.PI) / 180);

/**
 * A region's landmark (the Old Road's cabin and bridge, the valley's farm...)
 * stands small at the back corner of its hex, never on the middle, which is
 * where a unit stands.
 */
export const LANDMARK = { dx: 0.17, dz: -0.24, scale: 0.6 };

/**
 * Every unit's and raider party's spot: `{ dx, dz, alone }`. ALONE (the only
 * piece on a hex with nothing built on it) a party stands in the MIDDLE with
 * its people spread over the hex (playtest 11, Matt: "if they're alone on a
 * hex, stand in the center"). Otherwise the middle belongs to the town,
 * village or camp, and the parties stand round it, inside the hex.
 * @returns {Map<string, {dx: number, dz: number, alone: boolean}>} by
 *   `unit:<id>` / `raider:<id>`
 */
export const pieceSpots = (civ) => {
  const cityHexes = new Set((civ.city?.hexes || []).map(([a, b]) => key(a, b)));
  const built = new Set([
    ...cityHexes,
    ...(civ.villages || []).map((v) => key(v.q, v.r)),
    ...(civ.menaces || []).map((m) => key(m.q, m.r)),
    ...rivalGround(civ).keys(),
    ...(civ.forts || []),
  ]);
  const byHex = new Map();
  const add = (id, q, r) => {
    const k = key(q, r);
    if (!byHex.has(k)) byHex.set(k, []);
    byHex.get(k).push({ id, q, r });
  };
  (civ.units || []).forEach((u) => add(`unit:${u.id}`, u.q, u.r));
  (civ.raiders || []).forEach((x) => add(`raider:${x.id}`, x.q, x.r));

  const out = new Map();
  for (const [k, pieces] of byHex) {
    const [q, r] = k.split(',').map(Number);
    const busy = built.has(k) || isTown(q, r);
    if (pieces.length === 1 && !busy) {
      out.set(pieces[0].id, { dx: 0, dz: 0, alone: true });
      continue;
    }
    pieces.forEach((p, i) => {
      const a = SHARE_ANGLES[i % SHARE_ANGLES.length];
      out.set(p.id, { dx: SHARE_R * Math.cos(a), dz: SHARE_R * Math.sin(a), alone: false });
    });
  }
  return out;
};

/**
 * Where each of a party's people stands, relative to the party's spot, in
 * world units: alone, one per space of the hex's six so its strength reads;
 * sharing, a tight huddle that stays inside the hex.
 */
export const peopleSpots = (n, alone) => {
  if (alone) {
    if (n === 1) return [[0, 0]];
    return Array.from({ length: n }, (_, i) => {
      const a = Math.PI / 6 + (i * Math.PI) / 3;
      return [0.2 * Math.cos(a), 0.2 * Math.sin(a)];
    });
  }
  return [[0, 0], [-0.08, 0.06], [0.08, 0.06], [-0.06, -0.07], [0.06, -0.07]].slice(0, Math.max(1, n));
};

/**
 * ROUGH GROUND UNDER A PARTY. A forest or a mountain fills the middle of its
 * hex, and a party standing in the middle stood INSIDE it (the fight on the
 * mountain, playtest 12). So while a party stands there, the hex is drawn
 * flat and the forest or peak stands small at the back corner, opposite a
 * region's landmark: the party is in the open and the hex still reads as
 * what it is.
 */
export const ROUGH = { dx: -0.19, dz: -0.2, scale: 0.42 };
export const FLAT_OF = {
  'stone-mountain': 'stone', 'stone-hill': 'stone', 'stone-rocks': 'stone',
  'grass-hill': 'grass', 'grass-forest': 'grass', 'sand-rocks': 'sand', 'sand-desert': 'sand',
};
/** Hex keys a party stands on. */
export const partyHexes = (civ) => new Set([
  ...(civ.units || []).map((u) => key(u.q, u.r)),
  ...(civ.raiders || []).map((x) => key(x.q, x.r)),
]);

/**
 * Kenney's buildings are whole hex TILES with their own 0.2-high ground
 * plate. Stood on top of the hex's ground they made a raised step that a
 * party beside them sank into (the half-buried knight at Ashby, the pioneer
 * "inside the other graphic", playtest 12). Sunk by the plate's height, the
 * plate is flush with the ground and only the building stands.
 */
export const flushY = (top, model, scale = 1) => (/^(building-|bridge)/.test(model || '') ? top - 0.2 * scale : top);

/** Whose a party is, as a colour on the ground under it (playtest 12, Matt: "different colored hats"). */
// Raiders are ASH, a colour no people wears (playtest 13: rust-red read as
// the Iron Sons, twice).
export const SIDE_COLOURS = { ours: '#3b82f6', raiders: '#4a4440' };
